import React, { useRef } from 'react'
import { DashboardItem } from 'experiences/dashboard/DashboardRoot'
import { buildDefaultDashItem } from '../cards'

interface FallbackContextValues {
	getFallback: (cardKey: string) => DashboardItem
}
const defaultFallbackContext = {
	getFallback: () => buildDefaultDashItem()
}
export const FallbackContext = React.createContext<FallbackContextValues>(defaultFallbackContext)

const getNextFallback = (fallbacks: Array<DashboardItem>, registry: Record<string, string>): DashboardItem => {
	const claimedFallbacks = Object.values(registry)
	const next = fallbacks.find(item => !claimedFallbacks.includes(item.key))
	if (next) return next
	throw new Error('No unclaimed callbacks')
}

interface Props {
	fallbacks: DashboardItem[]
}

export const FallbackProvider: React.FC<React.PropsWithChildren<Props>> = ({ fallbacks, children }) => {
	/**
	 * This is a registry of <dashboard card keys, fallbacks>
	 * When a component requests a fallback, the next available is claimed and mapped here.
	 * Then on, if re-rendered, the registered value is always returned
	 */
	const registry = useRef<Record<string, string>>({})

	const getFallback = (cardKey: string) => {
		if (registry.current[cardKey] === undefined) {
			registry.current[cardKey] = getNextFallback(fallbacks, registry.current).key
		}
		return fallbacks.find(item => item.key === registry.current[cardKey]) || buildDefaultDashItem()
	}

	return <FallbackContext.Provider value={{ getFallback }}>{children}</FallbackContext.Provider>
}
